import { useColorScheme, useTheme } from '@mui/material/styles';
import type {
  AgCartesianAxesOptions,
  AgCartesianChartOptions
} from 'ag-charts-community';
import { AgCharts } from 'ag-charts-react';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';

import {
  formatLargeNumber,
  hasDataGreaterThanZero
} from '@/utils/Common.utils';

import { useChartLocationCheck } from '../charts.hooks';
import { getAgChartsTheme, noDataRenderer } from '../charts.utils';
import type { HorizontalBarChartProps } from './HorizontalBarChart.types';

const HorizontalBarChart = (props: HorizontalBarChartProps) => {
  const { data, series, handleChartClick, height } = props;
  const { mode: themeMode } = useColorScheme();
  const onReportsPage = useChartLocationCheck();
  const theme = useTheme();

  const { pathname } = useLocation();

  const hasVisibleData = useMemo(
    () => hasDataGreaterThanZero(data || []),
    [data]
  );

  const getFormattedAxes = useCallback((): AgCartesianAxesOptions => {
    if (!series?.length) {
      return {};
    }
    return {
      y: {
        type: 'category',
        position: 'left',
        line: {
          enabled: false
        },
        maxThicknessRatio: 0.3,
        label: {
          wrapping: onReportsPage ? 'always' : 'never',
          truncate: !onReportsPage,
          fontSize: 8,
          color: onReportsPage ? 'black' : undefined
        }
      },
      x: {
        type: 'number',
        position: 'bottom',
        line: {
          enabled: false
        },
        gridLine: {
          style: [
            {
              stroke: pathname.includes('reports')
                ? 'lightgray'
                : theme.vars.palette.border.light
            }
          ]
        },
        label: {
          formatter: (params: { value: number }) => {
            return formatLargeNumber(params.value);
          }
        }
      }
    };
  }, [onReportsPage, pathname, series, theme]);

  const options: AgCartesianChartOptions = {
    data: hasVisibleData ? data : [],
    series,
    background: {
      fill: 'transparent'
    },
    axes: getFormattedAxes(),
    theme: getAgChartsTheme(themeMode),
    animation: {
      enabled: true
    },
    legend: {
      enabled: false
    },
    listeners: {
      seriesNodeClick: ({ datum }) => {
        handleChartClick?.(datum.name);
      }
    },
    padding: {
      bottom: 0
    },
    overlays: {
      noData: {
        renderer: () => noDataRenderer()
      }
    },

    height
  };

  return (
    <AgCharts
      style={{
        height: height ?? '100%',
        borderRadius: '4px'
      }}
      options={options}
    />
  );
};

export default HorizontalBarChart;
